import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';
import { fetchPersonalContactsStatus } from '../../lib/personalContacts';
import useModuleAccess from '../../hooks/useModuleAccess';
import ResponsiveRow from '../shared/ResponsiveRow';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import RouteGuard from '../shared/RouteGuard';
import Modal3D from '../ui/Modal3D';
import Button3D from '../ui/Button3D';
import AccountSettingsView from './AccountSettingsView';
import ServicesSettingsView from './ServicesSettingsView';
import MyContactsSettingsView from './MyContactsSettingsView';
import PersonalNotificationPreferencesView from './PersonalNotificationPreferencesView';
import MySignatureView from './signatures/MySignatureView';
import EnterKeyPreferenceCard from './EnterKeyPreferenceCard';
import SelfHelpPreferenceCard from './SelfHelpPreferenceCard';
import ConversationPrivacyCard from './ConversationPrivacyCard';
import SignInSecurityPanel from './SignInSecurityPanel';
import DisplayThemeModal from '../modals/DisplayThemeModal';
import TeamsMirrorSettings from '../integrations/teams/TeamsMirrorSettings';
import { INTEGRATION_KEYS, canUseIntegration, useIntegrationAccess } from '../../lib/integrations/integrationGate';
import {
    SETTINGS_NODES, breadcrumbTrail, isNodeVisible, levelDepth, levelKeyFor, settingsKeyFromPath, settingsPath,
} from './settingsTree';
import HoverHelp from '../shared/HoverHelp';

// BL-177 Step 2j (E): Settings as a grid of 3D tiles that slide into each other. No left menu.
//
//   Settings (tiles) -> an area's own tiles -> ... -> the destination: a 3D modal (ui/Modal3D) holding that group's toggles.
//   Profile, My Contacts and Services are big editors, so they open as a page inside the same breadcrumb frame instead.
//   A breadcrumb ("Settings > Communications > Typing & sending") steps back at any level: each crumb is a link, the last is plain text.
//   Every level has its own URL (see settingsTree.js), the modal is part of the URL, closing a modal goes up one level.
//   Sliding uses CSS (settingsNav.css); with prefers-reduced-motion nothing slides and no slide class is rendered.
//
// Permission gating is exactly what it was: /settings, Profile, Sign-in & security and Display need Settings: view for the "user"
// role; Services needs Settings: edit; My Contacts shows while it is on (or for admins); Signature is not for the "user" role;
// Notifications and the rest of Communications stay open to everyone, as My notifications always was.

const SLIDE_MS = 280;
const GUARDED_FOR_USER_ROLE = ['', 'profile', 'security', 'display'];

// A modal that sits behind the "user" role's Settings: view check shows nothing at all while that check runs or when it fails
// (the tiles level underneath already says "You don't have access.").
function ViewGate({ children }) {
    const { allowed, loading } = useModuleAccess('settings', 'view');
    if (loading || !allowed) return null;
    return children;
}

function ModalGate({ node, isUser, children }) {
    return isUser && GUARDED_FOR_USER_ROLE.includes(node.key) ? <ViewGate>{children}</ViewGate> : children;
}

function Guard({ node, isUser, children }) {
    if (node.key === 'services') return <RouteGuard module="settings" action="edit" isUser={isUser}>{children}</RouteGuard>;
    if (isUser && GUARDED_FOR_USER_ROLE.includes(node.key)) return <RouteGuard module="settings" action="view" isUser>{children}</RouteGuard>;
    return children;
}

function Breadcrumb({ trail }) {
    return (
        <nav aria-label="Breadcrumb" className="ia-set__crumbs" data-testid="settings-breadcrumb">
            <ResponsiveRow className="ia-set__crumbs-row">
            <ol>
                {trail.map((step, index) => {
                    const last = index === trail.length - 1;
                    return (
                        <li key={step.key || 'settings'} className="ia-set__crumb-item">
                            {last ? (
                                <span aria-current="page" className="ia-set__crumb-current" data-testid="breadcrumb-current">{step.crumb}</span>
                            ) : (
                                <Link to={step.path} className="ia-btn3d ia-btn3d--pill ia-set__crumb" data-breadcrumb-step={step.key || 'settings'}>{step.crumb}</Link>
                            )}
                            {!last && <span aria-hidden="true" className="ia-set__crumb-sep">›</span>}
                        </li>
                    );
                })}
            </ol>
            </ResponsiveRow>
        </nav>
    );
}

function Tile({ node, from, interactive }) {
    const body = (
        <>
            <span aria-hidden="true" className="ia-set__tile-icon">{node.icon}</span>
            <span className="ia-set__tile-text">
                <span className="ia-set__tile-title">{node.tileTitle || node.title}</span>
                <span className="ia-set__tile-blurb">{node.blurb}</span>
            </span>
            <span aria-hidden="true" className="ia-set__tile-chevron">›</span>
        </>
    );
    if (!interactive) return <span className="ia-set__tile ia-depth-card" style={{ boxShadow: 'var(--ia-3d-card-depth)' }} data-settings-tile={node.key}>{body}</span>;
    return (
        <Link to={settingsPath(node.key)} state={{ settingsFrom: from }} className="ia-set__tile ia-depth-card" style={{ boxShadow: 'var(--ia-3d-card-depth)' }} data-settings-tile={node.key}>{body}</Link>
    );
}

function TilesLevel({ node, context, interactive = true }) {
    const tiles = node.children.map((key) => SETTINGS_NODES[key]).filter((child) => isNodeVisible(child, context));
    return (
        <ul className="ia-set__grid" data-testid="settings-grid">
            {tiles.map((child) => (
                <li key={child.key} className="ia-set__cell">
                    <Tile node={child} from={settingsPath(node.key)} interactive={interactive} />
                </li>
            ))}
        </ul>
    );
}

function PageFrame({ theme, children }) {
    return (
        <div data-theme={theme} className="ia-set__page" data-testid="settings-page">
            {children}
        </div>
    );
}

export default function SettingsNavigator({
    isUser = false,
    canManageServices = false,
    isOrgAdmin = false,
    isPlatformSuperAdmin = false,
    userId = null,
    orgId = null,
    platformDoorContext = null,
    headerBrandEntityId = '',
    userEmail = '',
    theme = 'light',
    accountSettings,
    setAccountSettings,
    triggerAutosave,
    onThemeSettingsChange,
    client = supabaseClient,
}) {
    const location = useLocation();
    const navigate = useNavigate();
    const reduced = usePrefersReducedMotion();
    const isAdmin = Boolean(isOrgAdmin || isPlatformSuperAdmin);
    const canManageMyContacts = isAdmin;
    const [myContactsOn, setMyContactsOn] = useState(false);
    // Teams PR A: the Teams mirror tile and popup exist only while Teams is on for this person (closed while the answer loads).
    const teamsAccess = useIntegrationAccess(INTEGRATION_KEYS.MICROSOFT_TEAMS, userId, client);
    const teamsEnabled = canUseIntegration(teamsAccess);

    useEffect(() => {
        let cancelled = false;
        fetchPersonalContactsStatus(client).then((status) => { if (!cancelled) setMyContactsOn(status.enabled); });
        return () => { cancelled = true; };
    }, [client]);

    // Old link: /settings?tab=communications&section=templates now lives in the Admin Center (kept exactly as before).
    useEffect(() => {
        if (location.pathname.replace(/\/+$/, '') !== '/settings') return;
        const params = new URLSearchParams(location.search);
        if (params.get('tab') === 'communications' && params.get('section') === 'templates') {
            navigate('/admin/email-channel?tab=templates', { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    const key = settingsKeyFromPath(location.pathname);
    const node = key === null ? null : SETTINGS_NODES[key];
    const levelKey = node ? levelKeyFor(node.key) : '';
    const levelNode = SETTINGS_NODES[levelKey] || SETTINGS_NODES[''];
    const depth = levelDepth(levelNode.key);
    const context = { isUser, canManageServices, canManageMyContacts, myContactsOn, teamsEnabled };

    // ---- the slide: remember the level we were on; when the level changes, the old tiles slide out while the new level slides in.
    const lastLevel = useRef({ key: levelNode.key, depth });
    const slideTimer = useRef(null);
    const [slide, setSlide] = useState(null); // { dir, leavingKey, token }
    useLayoutEffect(() => {
        const last = lastLevel.current;
        if (last.key === levelNode.key) return;
        lastLevel.current = { key: levelNode.key, depth };
        clearTimeout(slideTimer.current);
        if (reduced) { setSlide(null); return; }
        const leavingNode = SETTINGS_NODES[last.key];
        const next = { dir: depth >= last.depth ? 'forward' : 'back', leavingKey: leavingNode && leavingNode.kind === 'tiles' ? leavingNode.key : null, token: levelNode.key };
        setSlide(next);
        slideTimer.current = setTimeout(() => setSlide((current) => (current && current.token === next.token ? null : current)), SLIDE_MS);
    }, [levelNode.key, depth, reduced]);
    useEffect(() => () => clearTimeout(slideTimer.current), []);

    const closeModal = useCallback(() => {
        if (!node) return;
        const parent = settingsPath(node.parent);
        if (location.state && location.state.settingsFrom === parent) navigate(-1);
        else navigate(parent, { replace: true });
    }, [node, location.state, navigate]);

    if (!node) return <Navigate to="/settings" replace />;
    // The "user" role never had a My signature page: it went back to the Settings home.
    if (isNodeVisible(node, context) === false && node.hiddenForUserRole) return <Navigate to={settingsPath(node.parent)} replace />;
    // Teams mirror while Teams is off (or not assigned): back to Communications, once the answer is in.
    if (node.requiresTeams && !teamsAccess.loading && !teamsEnabled) return <Navigate to={settingsPath(node.parent)} replace />;

    const trail = breadcrumbTrail(node.key);
    const slideClass = slide && !reduced ? ` ia-set__pane--in-${slide.dir}` : '';
    const leavingNode = slide && !reduced && slide.leavingKey !== null ? SETTINGS_NODES[slide.leavingKey] : null;

    let pageBody = null;
    if (levelNode.kind === 'tiles') {
        pageBody = <TilesLevel node={levelNode} context={context} />;
    } else if (levelNode.key === 'profile') {
        pageBody = (
            <PageFrame theme={theme}>
                <AccountSettingsView accountSettings={accountSettings} setAccountSettings={setAccountSettings} triggerAutosave={triggerAutosave} userId={userId} theme={theme} />
            </PageFrame>
        );
    } else if (levelNode.key === 'my-contacts') {
        pageBody = (
            <PageFrame theme={theme}>
                <MyContactsSettingsView userId={userId} orgId={orgId} isOrgAdmin={Boolean(isOrgAdmin)} isPlatformSuperAdmin={Boolean(isPlatformSuperAdmin)} />
            </PageFrame>
        );
    } else if (levelNode.key === 'services') {
        pageBody = (
            <PageFrame theme={theme}>
                <ServicesSettingsView orgId={orgId} userId={userId} theme={theme} />
            </PageFrame>
        );
    }

    let modal = null;
    if (node.kind === 'modal') {
        const done = <Button3D variant="go" onClick={closeModal}>Done</Button3D>;
        let content = null;
        if (node.key === 'communications/notifications') content = <PersonalNotificationPreferencesView userId={userId} orgId={orgId} theme={theme} isAdmin={isAdmin} embedded />;
        else if (node.key === 'communications/signature') content = (
            <MySignatureView
                orgId={orgId}
                userId={userId}
                platformDoorContext={platformDoorContext}
                headerBrandEntityId={headerBrandEntityId}
            />
        );
        else if (node.key === 'communications/typing-sending') content = (
            <div className="flex flex-col gap-4">
                <EnterKeyPreferenceCard client={client} userId={userId} />
                {/* #656: Self-Help is a per-person On/Off only; it sits next to the Enter-key setting. No company control exists or is added here. */}
                <SelfHelpPreferenceCard client={client} userId={userId} />
            </div>
        );
        else if (node.key === 'communications/conversation-privacy') content = <ConversationPrivacyCard client={client} userId={userId} orgId={orgId} />;
        else if (node.key === 'security') content = <SignInSecurityPanel userEmail={userEmail} />;
        else if (node.key === 'communications/teams-mirror') content = (
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                    <span>Microsoft Teams connection</span>
                    <HoverHelp helpId="settings.teams-mirror" userId={userId} content={{ title: 'Teams mirror', body: 'Connect your own Microsoft Teams account to choose which chats to mirror and how new-chat alerts appear. Company and Microsoft 365 permissions still apply. Nothing is mirrored until you choose it.' }} />
                </div>
                <TeamsMirrorSettings client={client} userId={userId} locationSearch={location.search} />
                <Link to="/team-spaces" className="ia-btn3d ia-btn3d--primary self-start" data-testid="teams-open-my-teams">My teams</Link>
            </div>
        );

        if (node.requiresTeams && !teamsEnabled) {
            modal = null;
        } else if (node.key === 'display') {
            modal = (
                <ModalGate node={node} isUser={isUser}>
                    <DisplayThemeModal portal onClose={closeModal} settings={accountSettings || {}} onSettingsChange={onThemeSettingsChange || (() => {})} surfaceTheme={theme} />
                </ModalGate>
            );
        } else {
            modal = (
                <ModalGate node={node} isUser={isUser}>
                    <Modal3D
                        portal
                        size={node.size || 'medium'}
                        title={node.title}
                        subtitle={node.subtitle}
                        onClose={closeModal}
                        className="ia-settings-popup"
                        testId="settings-modal"
                        theme={theme}
                        data-settings-modal={node.key}
                        actions={done}
                    >
                        {content}
                    </Modal3D>
                </ModalGate>
            );
        }
    }

    return (
        <div data-theme={theme} className="ia-set" data-testid="settings-navigator" data-settings-level={levelNode.key || 'settings'}>
            <Breadcrumb trail={trail} />
            <div className="ia-set__viewport">
                {leavingNode && (
                    <div className={`ia-set__pane ia-set__pane--leaving ia-set__pane--out-${slide.dir}`} data-settings-pane="leaving" aria-hidden="true" inert="">
                        <header className="ia-set__head">
                            <h2 className="ia-set__title">{leavingNode.title}</h2>
                            <p className="ia-set__subtitle">{leavingNode.subtitle}</p>
                        </header>
                        <TilesLevel node={leavingNode} context={context} interactive={false} />
                    </div>
                )}
                <div key={levelNode.key} className={`ia-set__pane${slideClass}`} data-settings-pane="current" data-settings-slide={slide && !reduced ? slide.dir : 'none'}>
                    <header className="ia-set__head">
                        <h2 className="ia-set__title" data-testid="settings-level-title">{levelNode.title}</h2>
                        <p className="ia-set__subtitle">{levelNode.subtitle}</p>
                    </header>
                    <Guard node={levelNode} isUser={isUser}>
                        {pageBody}
                    </Guard>
                </div>
            </div>
            {modal}
        </div>
    );
}
